import React, { useEffect, useState, useCallback } from 'react';
import { firestore, storage } from '../Firebase';
import toast, { Toaster } from 'react-hot-toast';
import { Col, Container, Row, OverlayTrigger, Tooltip, Tab, Tabs } from 'react-bootstrap';
import HeaderAdmin from './HeaderAdmin';
import { useParams } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';
import DropzonePage from './DropzonePage';
import PageAssets from './PageAssets';
import { uploadImageToStorage } from '../utils/storageUpload';

const PageEdit = () => {
  const { pageID } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localPageData, setLocalPageData] = useState({
    title: '',
    html: '',
    css: '',
    js: '',
    hideHeader: false,
    thumbnail: '',
  });
  const [activeTab, setActiveTab] = useState('settings');

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const doc = await firestore.collection('pages').doc(pageID).get();
        if (doc.exists) {
          const data = doc.data();
          // Migration: if content exists but html doesn't, use content as html
          const pageData = {
            ...data,
            html: data.html || data.content || '',
            css: data.css || '',
            js: data.js || '',
            hideHeader: data.hideHeader || false,
          };
          setPageData(pageData);
          setLocalPageData(pageData);
        } else {
          toast.error('No such page found.');
        }
      } catch (error) {
        toast.error('Error fetching page.');
      } finally {
        setLoading(false);
      }
    };

    if (pageID) {
      fetchPage();
    }
  }, [pageID]);

  const updatePage = useCallback(
    (dataToUpdate) => {
      const updatedData = { ...dataToUpdate };
      firestore
        .collection('pages')
        .doc(pageID)
        .set(updatedData, { merge: true })
        .then(() => {
          toast.success('Page updated successfully!');
          setPageData((prev) => ({ ...prev, ...updatedData }));
        })
        .catch((error) => {
          toast.error('Error updating page: ' + error.message);
        });
    },
    [pageID]
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLocalPageData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = () => {
    updatePage(localPageData);
  };

  const onDropHandler = async ({ name, uri }) => {
    if (!name || !uri) return;

    setLoading(true);
    try {
      const downloadURL = await uploadImageToStorage(storage, uri);

      updatePage({ thumbnail: downloadURL });
      setLocalPageData((prev) => ({ ...prev, thumbnail: downloadURL }));

      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(
        'An error occurred while uploading the file. Please try again.'
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid>
        <Row>
          <HeaderAdmin />
          <div className="overlay">
            <BeatLoader color="#36d7b7" size={15} loading={loading} />
          </div>
        </Row>
      </Container>
    );
  }

  if (!pageData) {
    return (
      <Container fluid>
        <Row>
          <HeaderAdmin />
          <div className="text-center mt-5">Page not found.</div>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row>
        <HeaderAdmin />
        <Toaster />
      </Row>
      <Row>
        <Col>
          <div className="h1">
            {localPageData?.title || ''} <span className="h5">Edit Page</span>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="bg-white rounded-3 shadow w-100 mb-3 overflow-hidden">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3 bg-light border-bottom"
              style={{ padding: '10px 10px 0' }}
            >
              <Tab eventKey="settings" title="Settings">
                <div className="p-3">
                  <Row>
                    <Col md={6}>
                      <OverlayTrigger
                        placement={'right'}
                        overlay={<Tooltip>Title of the page.</Tooltip>}
                      >
                        <div className="form-floating mb-3">
                          <input
                            id="page-title"
                            className="form-control"
                            name="title"
                            type="text"
                            value={localPageData?.title || ''}
                            onChange={handleInputChange}
                          />
                          <label htmlFor="page-title">Title</label>
                        </div>
                      </OverlayTrigger>

                      <div className="form-check form-switch mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="hideHeader"
                          name="hideHeader"
                          checked={localPageData?.hideHeader || false}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="hideHeader">
                          Hide Default Header (allows custom positioning via CSS)
                        </label>
                      </div>
                    </Col>
                    <Col md={6}>
                      <OverlayTrigger
                        placement={'left'}
                        overlay={
                          <Tooltip>
                            Update your thumbnail image by dragging and dropping.
                          </Tooltip>
                        }
                      >
                        <div>
                          <DropzonePage
                            onDropHandler={onDropHandler}
                            imagePath={localPageData?.thumbnail || ''}
                          />
                        </div>
                      </OverlayTrigger>
                    </Col>
                  </Row>
                  <div className="text-end mt-3">
                    <button className="btn btn-primary" onClick={handleSave}>
                      Save Settings
                    </button>
                  </div>
                </div>
              </Tab>
              <Tab eventKey="html" title="HTML">
                <div className="p-3">
                  <h5 className="mb-2 text-muted">Page Structure</h5>
                  <textarea
                    className="form-control"
                    name="html"
                    rows={20}
                    value={localPageData?.html || ''}
                    onChange={handleInputChange}
                    style={{ fontFamily: 'monospace', fontSize: '14px' }}
                    placeholder="<div>Hello World</div>"
                  />
                  <div className="mt-3 text-end">
                    <button className="btn btn-primary" onClick={handleSave}>
                      Save HTML
                    </button>
                  </div>
                </div>
              </Tab>
              <Tab eventKey="css" title="CSS">
                <div className="p-3">
                  <h5 className="mb-2 text-muted">Custom Styles</h5>
                  <textarea
                    className="form-control"
                    name="css"
                    rows={20}
                    value={localPageData?.css || ''}
                    onChange={handleInputChange}
                    style={{ fontFamily: 'monospace', fontSize: '14px' }}
                    placeholder=".my-class { color: red; }"
                  />
                  <div className="mt-3 text-end">
                    <button className="btn btn-primary" onClick={handleSave}>
                      Save CSS
                    </button>
                  </div>
                </div>
              </Tab>
              <Tab eventKey="js" title="JavaScript">
                <div className="p-3">
                  <h5 className="mb-2 text-muted">Custom Scripts</h5>
                  <div className="alert alert-warning py-2">
                    <small>
                      <strong>Warning:</strong> Scripts run in the global scope. Use an IIFE or careful variable naming. 
                      Access the root element via <code>rootElement</code> variable.
                      Return a cleanup function to handle unmounting.
                    </small>
                  </div>
                  <textarea
                    className="form-control"
                    name="js"
                    rows={15}
                    value={localPageData?.js || ''}
                    onChange={handleInputChange}
                    style={{ fontFamily: 'monospace', fontSize: '14px' }}
                    placeholder={`// Example:
const btn = rootElement.querySelector('button');
const handleClick = () => alert('Clicked!');
if (btn) btn.addEventListener('click', handleClick);

// Cleanup function
return () => {
  if (btn) btn.removeEventListener('click', handleClick);
};`}
                  />
                  <div className="mt-3 text-end">
                    <button className="btn btn-primary" onClick={handleSave}>
                      Save JavaScript
                    </button>
                  </div>
                </div>
              </Tab>
              <Tab eventKey="assets" title="Assets">
                <div className="p-3">
                  <PageAssets pageID={pageID} />
                </div>
              </Tab>
            </Tabs>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PageEdit;
